import _ from 'underscore';
import { CircleCheckBig, Copy } from 'lucide-react';
import { Box, Button, styled, Typography } from '@mui/material';
import { ChangeEvent, FC, memo, useEffect, useState } from 'react';

import Checkbox from 'components/checkbox';
import KebabMenu from 'components/kebab-menu';
import ListRow from 'components/list/list-row';
import ListCell from 'components/list/list-cell';
import List, { HeadCell } from 'components/list/list';
import ScenarioFeedRowBody from './scenario-feed-row-body';
import ScenarioCloneDialog from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-clone-dialog';

import { useR2ScenarioState } from 'views/bluviu-nutrition/features/scenarios-v2/context/r2-scenario-context';
import { useScenarioResultState } from 'views/bluviu-nutrition/features/scenarios-v2/context/scenario-result-context';
import { Summary } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-result.type';
import {
  buildScenarioAwardBadges,
  getVisibleAwardKeys,
} from 'views/bluviu-nutrition/features/scenarios-v2/utils/compare-result-utils';
import ScenarioAwardIcons from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-result-tab/scenario-award-icons';
import FormatMetricValue from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-result-tab/format-metric-value';
import ScenarioFeedTooltip from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-result-tab/scenario-result-feed-tooltip';

type Props = {
  selectedFeedPlans: Summary[];
  setSelectedFeedPlans: React.Dispatch<React.SetStateAction<Summary[]>>;
};

const ScenarioFeedList: FC<Props> = ({ selectedFeedPlans, setSelectedFeedPlans }) => {
  const {
    r2ScenarioQuery: { data: scenario },
  } = useR2ScenarioState();

  const {
    getScenarioPostProcessQuery: { data: postProcessData, isLoading },
    setSelectedPrograms,
    selectedPrograms,
  } = useScenarioResultState();

  const [rows, setRows] = useState<Summary[]>([]);

  // #region EFFECTS

  useEffect(() => {
    setRows(_.sortBy(postProcessData?.siteSummary ?? [], 'feedPlan'));
  }, [postProcessData]);

  // #endregion

  const allSelected = rows.length > 0 && selectedFeedPlans.length === rows.length;
  const indeterminate = selectedFeedPlans.length > 0 && selectedFeedPlans.length < rows.length;

  const handleSelectAll = (_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (checked) {
      const uniqueRows = _.uniq(rows, false, (row) => row.id);
      setSelectedFeedPlans(uniqueRows);
      setSelectedPrograms(uniqueRows.map((row) => row.id));
      return;
    }

    setSelectedFeedPlans([]);
    setSelectedPrograms([]);
  };

  const handleSelectRow = (item: Summary) => (_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setSelectedFeedPlans((prev) => {
      if (!checked) {
        return prev.filter((row) => row.id !== item.id);
      }

      const withNewItem = [...prev, item];
      return _.uniq(withNewItem, false, (row) => row.id);
    });

    setSelectedPrograms((prev) => {
      const newSelected = checked ? _.uniq([...prev, item.id]) : prev.filter((rowId) => rowId !== item.id);
      return newSelected;
    });
  };

  const KebabMenuItem = styled(Button)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    color: theme.palette.darkBlue.main,
    textTransform: 'none',
    padding: theme.spacing(0),
  }));

  const columns: HeadCell[] = [
    {
      id: 'checkAll',
      autoWidth: true,
      disableSort: true,
      label: <Checkbox checked={allSelected} indeterminate={indeterminate} onChange={handleSelectAll} />,
    },
    {
      id: 'feedPlan',
      align: 'left',
      label: 'Feed Program',
    },
    {
      id: 'badges',
      align: 'left',
      label: '',
      disableSort: true,
    },
    {
      id: 'finalWeightGrams',
      align: 'right',
      label: 'Final weight',
    },
    {
      id: 'bFcr',
      align: 'right',
      label: 'bFCR',
    },
    {
      id: 'averageFeedCostPerKgFeed',
      align: 'right',
      label: 'Avg. feed cost',
    },
    {
      id: 'actions',
      align: 'right',
      label: '',
    },
  ];

  return (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      <List
        rows={rows}
        options={columns}
        isLoading={isLoading}
        orderBy={'feedPlan'}
        sort={setRows}
        noResult={{
          show: !rows?.length,
          text: 'No Results',
        }}>
        {rows.map((item) => {
          const badges = buildScenarioAwardBadges(item, rows);
          const visibleAwardKeys = getVisibleAwardKeys(badges);

          return (
            <ListRow
              key={item.feedPlan}
              body={{ span: columns.length + 1, options: [], children: <ScenarioFeedRowBody summary={item} badges={badges} /> }}>
              <ListCell>
                <Checkbox
                  dark
                  checked={selectedFeedPlans.some((r) => r.feedPlan === item.feedPlan)}
                  onChange={handleSelectRow(item)}
                />
              </ListCell>
              <ListCell align="left">
                <Box display="flex" alignItems="center" gap={6}>
                  <Typography variant="body2"> {item.feedPlan}</Typography>
                </Box>
              </ListCell>
              <ListCell>
                <Box display="flex" alignItems="center" gap={6}>
                  <ScenarioFeedTooltip feedPlan={item.feedPlan} feeds={item.feeds} display="icon" />
                  <ScenarioAwardIcons visibleAwardKeys={visibleAwardKeys} />
                </Box>
              </ListCell>
              <ListCell align="right">
                <Typography variant="body2">
                  <FormatMetricValue value={item.finalWeightGrams} valueKey="finalWeightGrams" />
                </Typography>
              </ListCell>
              <ListCell align="right">
                <Typography variant="body2">
                  <FormatMetricValue value={item.bFcr} valueKey="bFcr" />
                </Typography>
              </ListCell>
              <ListCell align="right">
                <Typography variant="body2">
                  <FormatMetricValue
                    value={item.averageFeedCostPerKgFeed}
                    valueKey="averageFeedCostPerKgFeed"
                    currency={postProcessData?.currency}
                  />
                </Typography>
              </ListCell>
              <ListCell align="right">
                <KebabMenu dark>
                  <Box display="flex" flexDirection="column">
                    <KebabMenuItem variant="text" startIcon={<CircleCheckBig size={20} />} onClick={() => { }}>
                      Apply to site
                    </KebabMenuItem>
                    {scenario && (
                      <ScenarioCloneDialog
                        scenario={scenario}
                        selectedFeedProgramIds={[item.id]}
                        button={
                          <KebabMenuItem variant="text" startIcon={<Copy size={20} />}>
                            Clone feed program
                          </KebabMenuItem>
                        }
                      />
                    )}
                  </Box>
                </KebabMenu>
              </ListCell>
            </ListRow>
          );
        })}
      </List>
    </Box>
  );
};

export default memo(ScenarioFeedList);
